import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState, useXNGSelector } from "../store";
import {
  ServiceProviderRef,
  ServiceProviderResponse,
  ServiceProviderType,
} from "../../profile-sdk";
import { selectLoggedInClientAssignment } from "./userProfileSlice";

type DataEntryProviderState = {
  provider: ServiceProviderResponse | null;
  /** True when the user has signed in as a Proxy Data Entry provider via the profile menu. */
  isSignedInAsDataEntryProvider: boolean;
};

const initialDataEntryProviderState: DataEntryProviderState = {
  provider: null,
  isSignedInAsDataEntryProvider: false,
};

/**
 * NOTE: We used to, at times, refer to the proxied user as the 'acting service provider'.
 * The team doesn't use this terminology anymore, so reflecting 'proxied' terminology in
 * the code instead is recommended.
 *
 * Reference updated definitions here in our Common Data Points article:
 * https://msb-tlm.atlassian.net/wiki/spaces/XL/pages/edit-v2/190906371
 */
export const dataEntryProviderSlice = createSlice({
  name: "data entry provider",
  initialState: initialDataEntryProviderState,
  reducers: {
    setDataEntryProvider: (state, action: PayloadAction<ServiceProviderResponse | null>) => {
      state.provider = action.payload;
    },
    setIsSignedInAsDataEntryProvider: (state, action: PayloadAction<boolean>) => {
      state.isSignedInAsDataEntryProvider = action.payload;
    },
  },
});

// SELECTORS, used to read state
export const selectDataEntryProvider = (state: RootState) => {
  return state.dataEntryProvider.provider;
};

export const selectUserIsCurrentlyProxying = (state: RootState) => {
  return Boolean(state.dataEntryProvider.provider);
};

// TODO: Phase out
export const selectUserIsSignedInAsDEP = (state: RootState) => {
  return Boolean(state.dataEntryProvider.provider);
};

export const selectIsSignedInAsDataEntryProvider = (state: RootState) => {
  return state.dataEntryProvider.isSignedInAsDataEntryProvider;
};

export function selectActingServiceProvider(state: RootState): ServiceProviderRef | undefined {
  const clientAssignment = selectLoggedInClientAssignment(state);

  const actingProvider = state.dataEntryProvider.provider ?? clientAssignment.serviceProviderProfile;

  return actingProvider;
}

export function selectActingServiceProviderType(state: RootState): ServiceProviderType[] {
  const serviceProviderType =
  state.dataEntryProvider.provider?.serviceProviderTypes ?? state.userResponse?.serviceProviderTypes;
  return serviceProviderType || [];
}
// ACTIONS, used to execute logic
export const { setDataEntryProvider, setIsSignedInAsDataEntryProvider } = dataEntryProviderSlice.actions;

export default dataEntryProviderSlice.reducer;
